import { create } from "zustand";

import type {
  DeployedUnit,
  Deployment,
  Phase,
  Posture,
  PostureReason,
  Propagation,
  PropagationChange,
  Side,
  TacticalRole,
} from "../../api/types";
import { k, t } from "../../i18n/i18n";
import { unitMissionName } from "../../i18n/vocabulary";
import { plural } from "../../plural";

/** Propagation des missions selon la posture et les rôles (docs/25) : textes de l'avis récapitulatif (cause →
 *  effets, posture avant / après, « pourquoi » doctrinal), repères des rôles tactiques et de l'origine des missions,
 *  état de l'avis et de la mise en évidence sur la carte. Le serveur décide de tout (missions, rôles, postures) ;
 *  le client ne fait que le dire. */

const PHASES: readonly Phase[] = ["T1", "T2", "T3"];

// --- Rôles tactiques ----------------------------------------------------------------------------------------

/** Libellé court du rôle tactique, porté par le symbole sur la carte (repère discret). */
const ROLE_SHORT: Record<TacticalRole, string> = {
  reco: k("Reco"),
  ech1: k("1er éch."),
  ech2: k("2e éch."),
  reserve: k("Rés."),
  appui: k("App."),
  soutien: k("Sout."),
};

/** Libellé du rôle tactique dans les phrases et la fiche. */
const ROLE_LONG: Record<TacticalRole, string> = {
  reco: k("reconnaissance"),
  ech1: k("1er échelon"),
  ech2: k("2e échelon"),
  reserve: k("réserve"),
  appui: k("appui"),
  soutien: k("soutien"),
};

/** Rôles d'une défense (registre A126) : ni reconnaissance, un 1er échelon défensif appuyé par un 2e échelon
 *  défensif, des renforts et une réserve. Une reconnaissance (dossier d'un serveur antérieur) se dit au 1er échelon
 *  défensif. */
const DEFENSIVE_ROLE_SHORT: Partial<Record<TacticalRole, string>> = {
  reco: k("1er éch. déf."),
  ech1: k("1er éch. déf."),
  ech2: k("2e éch. déf."),
};
const DEFENSIVE_ROLE_LONG: Partial<Record<TacticalRole, string>> = {
  reco: k("1er échelon défensif"),
  ech1: k("1er échelon défensif"),
  ech2: k("2e échelon défensif"),
};

/** Ennemi : premier échelon et échelon ultérieur. */
const ENI_ROLE_SHORT: Partial<Record<TacticalRole, string>> = { ech1: k("1er éch."), ech2: k("Éch. ult.") };
const ENI_ROLE_LONG: Partial<Record<TacticalRole, string>> = { ech1: k("1er échelon"), ech2: k("échelon ultérieur") };

/** Rôles amis d'une défense (registre A126), publiés par le serveur avec la posture de la carte. */
export function defensiveRoles(deployment: Pick<Deployment, "posture"> | null | undefined): boolean {
  return deployment?.posture?.defensive_roles === true;
}

/** Libellé court du rôle sous le symbole ; `defensive` : rôles d'une défense (registre A126). */
export function roleShort(role: TacticalRole | null | undefined, side: Side, defensive = false): string | null {
  if (!role) return null;
  const label = side === "ENI" ? ENI_ROLE_SHORT[role] : ((defensive ? DEFENSIVE_ROLE_SHORT[role] : undefined) ?? ROLE_SHORT[role]);
  return label ? t(label) : null;
}

/** Libellé du rôle dans les phrases et la fiche ; `defensive` : rôles d'une défense (registre A126). */
export function roleLong(role: TacticalRole | null | undefined, side: Side, defensive = false): string | null {
  if (!role) return null;
  const label = side === "ENI" ? ENI_ROLE_LONG[role] : ((defensive ? DEFENSIVE_ROLE_LONG[role] : undefined) ?? ROLE_LONG[role]);
  return label ? t(label) : null;
}

/** Rôle de la table du serveur (`recoAmi`…, `ech1`, `ech2`) → rôle tactique affiché. */
export function reasonRole(reason: PostureReason): TacticalRole | null {
  const map: Record<string, TacticalRole> = {
    recoAmi: "reco",
    ech1Ami: "ech1",
    ech2Ami: "ech2",
    renfortsAmi: "reserve",
    ech1: "ech1",
    ech2: "ech2",
  };
  return map[reason.role] ?? null;
}

// --- Postures -----------------------------------------------------------------------------------------------

const POSTURE_LABEL: Record<Posture, string> = {
  offensive: k("offensive"),
  defensive: k("défensive"),
  securisation: k("sécurisation"),
  assistance: k("assistance"),
  infiltration: k("infiltration"),
};

export function postureLabel(posture: Posture | null | undefined): string {
  return posture ? t(POSTURE_LABEL[posture]) : "—";
}

/** Postures par temps dites en un mot quand elles sont égales, sinon temps par temps (« T1 défensive, T2 … »). */
export function postureSpan(postures: Partial<Record<Phase, Posture | null>> | null | undefined): string {
  const values = PHASES.map((ph) => postures?.[ph] ?? null);
  if (values.every((v) => v === values[0])) return postureLabel(values[0]);
  return PHASES.map((ph, i) => `${ph} ${postureLabel(values[i])}`).join(", ");
}

/** « Posture AMI : défensive · ENI : offensive » d'un temps. */
export function postureAt(deployment: Pick<Deployment, "posture"> | null | undefined, phase: Phase): string | null {
  const info = deployment?.posture;
  if (!info) return null;
  return t("AMI : {ami} · ENI : {eni}", { ami: postureLabel(info.ami[phase]), eni: postureLabel(info.eni[phase]) });
}

/** Ligne « posture avant → après » de l'avis ; null si elle ne change pas. */
export function postureChange(propagation: Propagation): string | null {
  const change = propagation.posture;
  if (!change) return null;
  const ami = `${postureSpan(change.before.ami)} → ${postureSpan(change.after.ami)}`;
  const eni = `${postureSpan(change.before.eni)} → ${postureSpan(change.after.eni)}`;
  return t("Posture AMI : {ami} · ENI : {eni}", { ami, eni });
}

// --- Pourquoi -----------------------------------------------------------------------------------------------

/** Phrase doctrinale de la raison d'une mission propagée (Mémento tactique EDG, Base doc tactique BIA ; docs/25 §3). */
const WHY: Record<string, string> = {
  "AMI.defensive.front": k(
    "En défense, l'élément de tête du 1er échelon défensif jalonne puis freine l'ennemi en avant des positions, avant d'être recueilli.",
  ),
  "AMI.defensive.ech1": k("En défense, le 1er échelon défensif défend ses positions, sans esprit de recul."),
  "AMI.defensive.ech2": k(
    "En défense, le 2e échelon défensif tient la ligne d'arrêt puis contre-attaque pour rétablir la défense.",
  ),
  "AMI.defensive.reserve": k("En défense, la réserve se tient prête puis contre-attaque."),
  "AMI.offensive.reco": k("En attaque, la reconnaissance reconnaît en avant du 1er échelon, à chaque temps."),
  "AMI.offensive.ech1": k("En attaque, le 1er échelon conduit l'action principale."),
  "AMI.offensive.ech2": k("En attaque, le 2e échelon appuie le 1er puis exploite."),
  "AMI.offensive.reserve": k("En attaque, la réserve appuie l'action principale."),
  "AMI.securisation": k("En sécurisation, chaque unité contrôle sa zone ; la reconnaissance reconnaît, la réserve appuie."),
  "ENI.offensive.ech1": k("L'ennemi attaque : son 1er échelon débouche de sa base de départ vers nos positions."),
  "ENI.offensive.ech2": k("L'ennemi attaque : son échelon ultérieur appuie puis s'engage derrière son 1er échelon."),
  "ENI.defensive.ech1": k("L'ennemi défend : son 1er échelon tient ses positions puis freine."),
  "ENI.defensive.ech2": k("L'ennemi défend : son échelon ultérieur contre-attaque."),
  "ENI.infiltration": k("Face à notre sécurisation, l'ennemi s'infiltre."),
};

/** « Pourquoi » d'une mission propagée, en une phrase doctrinale. */
export function whyOf(reason: PostureReason | null | undefined): string | null {
  if (!reason) return null;
  const role = reason.front === "1" ? "front" : reasonRole(reason);
  const found = WHY[`${reason.side}.${reason.posture}.${role}`] ?? WHY[`${reason.side}.${reason.posture}`];
  return found ? t(found) : null;
}

/** « Pourquoi » d'une ligne de l'avis : celui de sa première mission propagée, sinon le rôle relu en profondeur. */
export function changeWhy(change: PropagationChange): string | null {
  const reason = change.missions.find((m) => m.reason)?.reason;
  const found = whyOf(reason);
  if (found) return found;
  if (change.role_before !== change.role_after && change.role_after) {
    return t("Rôle relu sur la carte, d'après la profondeur le long de l'axe : l'unité reçoit les missions de ce rôle.");
  }
  return null;
}

// --- Lignes de l'avis ---------------------------------------------------------------------------------------

/** Missions d'une ligne : « DÉFENDRE (T2, T3) », ou temps par temps quand elles diffèrent. */
export function missionsText(change: PropagationChange): string {
  const shown = change.missions.map((m) => ({ phase: m.phase, label: m.after ? unitMissionName(m.after) : t("Sans mission") }));
  if (!shown.length) return "";
  if (shown.every((m) => m.label === shown[0]!.label)) {
    return `${shown[0]!.label} (${shown.map((m) => m.phase).join(", ")})`;
  }
  return shown.map((m) => `${m.phase} ${m.label}`).join(" · ");
}

/** Effet d'une ligne : rôle gagné (« passe en 2e échelon »), puis missions ; `defensive` : rôles d'une défense. */
export function changeText(change: PropagationChange, defensive = false): string {
  const parts: string[] = [];
  if (change.role_before !== change.role_after && change.role_after) {
    parts.push(t("passe en {role}", { role: roleLong(change.role_after, change.side, defensive) ?? "" }));
  }
  const missions = missionsText(change);
  if (missions) parts.push(missions);
  return parts.join(" : ");
}

/** Cause de l'avis : « Mission de 3(FR)DIV en T1 : DÉFENDRE », « 3(FR)DIV déplacée en T1 ». */
export function causeText(propagation: Propagation): string {
  const { cause } = propagation;
  if (cause.kind === "move") return t("{unit} déplacée en {phase}", { unit: cause.designation, phase: cause.phase });
  const mission = cause.mission === null || cause.mission === undefined ? t("mission doctrinale") : cause.mission ? unitMissionName(cause.mission) : t("Sans mission");
  return t("Mission de {unit} en {phase} : {mission}", { unit: cause.designation, phase: cause.phase, mission });
}

/** Nombre de missions changées par la propagation. */
export function missionCount(propagation: Propagation): number {
  return propagation.changes.reduce((n, c) => n + c.missions.length, 0);
}

/** Titre de l'avis : missions mises à jour, sinon rôles relus, sinon posture inchangée (action attendue). */
export function headline(propagation: Propagation): string {
  const count = missionCount(propagation);
  if (count) {
    return plural(count, t("mission mise à jour pour rester cohérente"), t("missions mises à jour pour rester cohérentes"));
  }
  const roles = propagation.changes.filter((c) => c.role_before !== c.role_after).length;
  if (roles) return plural(roles, t("rôle relu sur la carte"), t("rôles relus sur la carte"));
  if (propagation.hint) return t("Posture inchangée");
  return causeText(propagation);
}

/** Phrase de l'action attendue (`hint`) : ce qui manque pour que la posture change. */
export function hintText(propagation: Propagation): string | null {
  const hint = propagation.hint;
  if (!hint) return null;
  const others = hint.others.map((o) => o.designation).join(", ");
  const side = hint.side === "ENI" ? t("ennemi") : t("ami");
  return t(
    "{agree} unité(s) de 1er échelon {side} sur {lead} en posture {posture} en {phase} : pour que l'échelon change de posture, choisissez aussi {mission} pour {others}.",
    {
      agree: hint.agree,
      lead: hint.lead,
      side,
      posture: postureLabel(hint.posture),
      phase: hint.phase,
      mission: hint.mission ? unitMissionName(hint.mission) : "",
      others,
    },
  );
}

/** L'avis a-t-il quelque chose à dire (effets, posture, action attendue) ? */
export function worthShowing(propagation: Propagation | null | undefined): propagation is Propagation {
  return Boolean(propagation && (propagation.changes.length || propagation.posture || propagation.hint));
}

/** Missions d'avant d'une ligne, pour l'annuler seule (`POST /missions/restore`). */
export function restoreItems(change: PropagationChange): { unit: string; phase: Phase; mission: string }[] {
  return change.missions.map((m) => ({ unit: change.unit, phase: m.phase, mission: m.before }));
}

// --- Origine de la mission (fiche) --------------------------------------------------------------------------

export type MissionOrigin = "manual" | "posture" | "doctrinal";

/** Origine de la mission d'une unité à un temps : choisie à la main, adaptée à la posture, ou doctrinale. */
export function missionOrigin(unit: DeployedUnit, phase: Phase): MissionOrigin | null {
  const position = unit.positions[phase];
  if (!position) return null;
  if (position.overridden) return "manual";
  if (position.reason) return "posture";
  return "doctrinal";
}

export function originLabel(origin: MissionOrigin): string {
  if (origin === "manual") return t("Modifiée par vous");
  if (origin === "posture") return t("Adaptée à la posture");
  return t("Doctrinale");
}

// --- État de l'avis et de la mise en évidence --------------------------------------------------------------

export interface PropagationEntry {
  id: number;
  propagation: Propagation;
  /** Annule toute l'écriture (historique de la carte, ⌘Z). */
  undo?: () => void;
  /** Action vers l'ordre recomposé (« Voir les changements »…). */
  order?: { label: string; run: () => void };
  /** Mention des ordres recomposés (`withOrders`). */
  detail?: string;
}

/** Unités mises en évidence sur la carte après une propagation (halo bref, trait vers la cause). */
export interface PropagationFocus {
  id: number;
  cause: string | null;
  keys: string[];
  at: number;
}

interface PropagationState {
  entry: PropagationEntry | null;
  focus: PropagationFocus | null;
  /** Lignes déjà annulées une à une dans l'avis affiché. */
  undone: string[];
  show: (entry: Omit<PropagationEntry, "id">) => void;
  close: () => void;
  markUndone: (unit: string) => void;
  /** Remet en évidence une seule unité (ligne cliquée). */
  spotlight: (unit: string) => void;
}

let nextId = 1;

export const usePropagation = create<PropagationState>((set) => ({
  entry: null,
  focus: null,
  undone: [],
  show: (entry) => {
    const id = nextId++;
    const keys = entry.propagation.changes.map((c) => c.unit);
    set({
      entry: { ...entry, id },
      undone: [],
      focus: keys.length ? { id, cause: entry.propagation.cause.unit, keys, at: performance.now() } : null,
    });
  },
  close: () => set({ entry: null }),
  markUndone: (unit) => set((s) => ({ undone: [...s.undone, unit] })),
  spotlight: (unit) => set({ focus: { id: nextId++, cause: null, keys: [unit], at: performance.now() } }),
}));
